/// <amd-module name="Build/Admin/Pool/PoolView" />
import { PoolHubViewKeys, ServiceIds, Telemetry } from "Build/Admin/Common/Constants";
import { HeaderBreadcrumbService } from "Build/Admin/Common/Services/HeaderBreadcrumbService";
import { IProjectDataService } from "Build/Admin/Common/Services/ProjectDataService";
import { PoolHub } from "Build/Admin/Pool/Components/PoolHub";
import { Flux } from "Build/Admin/Pool/Flux";
import { PoolSignalRHub } from "Build/Admin/Pool/PoolSignalRHub";
import * as React from "react";
import { IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { IVssPageService } from "VSS/Platform/Page";
import { IVssPerformanceService } from "VSS/Platform/Performance";

export class PoolView extends VssComponent<IVssComponentProperties> {
    private _flux: Flux;
    private _breadcrumbService: HeaderBreadcrumbService;
    private _signalRHub: PoolSignalRHub;
    private _ttiMarked: boolean = false;
    private _dataReady: boolean = false;

    constructor(props: IVssComponentProperties, context: IVssComponentContext) {
        super(props, context);

        this._flux = new Flux(context.pageContext);

        this.state = { dataReady: this._getInitialDataReady() };

        this._flux.actionsHub.agentQueuePipelinesAuthorizedUpdated.addListener(this._markDataReady);
        this._flux.actionsHub.agentsUpdated.addListener(this._markDataReady);
        this._flux.actionsHub.agentPoolDescriptionUpdated.addListener(this._markDataReady);
        this._flux.actionsHub.elasticPoolLogsUpdated.addListener(this._markDataReady);

        this._flux.poolActionCreator.initializePool();

        this.initializeSignalR(context);

        this._breadcrumbService = context.pageContext.getService<HeaderBreadcrumbService>(ServiceIds.HeaderBreadcrumbService);
        this._breadcrumbService.refresh({
            agentPool: this._flux.storesHub.selectionStore.state.selectedAgentPool,
            agentQueue: this._flux.storesHub.selectionStore.state.selectedAgentQueue,
            agent: undefined
        });
    }

    public componentDidMount(): void {
        this._markTti();
    }

    public componentWillUnmount(): void {
        this._flux.actionsHub.agentQueuePipelinesAuthorizedUpdated.removeListener(this._markDataReady);
        this._flux.actionsHub.agentsUpdated.removeListener(this._markDataReady);
        this._flux.actionsHub.agentPoolDescriptionUpdated.removeListener(this._markDataReady);
        this._flux.actionsHub.elasticPoolLogsUpdated.removeListener(this._markDataReady);

        this._flux.dispose();

        if (this._signalRHub) {
            this._signalRHub.dispose();
        }
    }

    public componentDidUpdate(): void {
        this._markTti();
    }

    public render(): JSX.Element {
        return (
            <PoolHub
                actionCreator={this._flux.poolActionCreator}
                graphActionCreator={this._flux.poolGraphActionCreator}
                storesHub={this._flux.storesHub}
            />
        );
    }

    private initializeSignalR(context: IVssComponentContext): void {
        const projectService = context.pageContext.getService<IProjectDataService>("IProjectDataService");
        const vssPageService = context.pageContext.getService<IVssPageService>("IVssPageService");
        const vssPageData = vssPageService.getData();

        if (vssPageData) {
            const projectData = projectService.getProjectData();

            const hostInformation = {
                hostId: vssPageData.hostId,
                isHosted: vssPageData.isHosted,
                hostPath: vssPageData.hostPath,
                projectId: projectData && projectData.id
            };

            this._signalRHub = new PoolSignalRHub({
                hostInformation,
                agentActions: this._flux.actionsHub.agentActions,
                actionCreator: this._flux.poolActionCreator,
                signalRActions: this._flux.actionsHub.signalRActions,
                pageContext: context.pageContext,
                removeApiPath: !projectData
            });

            if (this._flux.storesHub.selectionStore.state.selectedAgentQueue) {
                this._signalRHub.listen(this._flux.storesHub.selectionStore.state.selectedAgentQueue.pool.id);
            } else if (this._flux.storesHub.selectionStore.state.selectedAgentPool) {
                this._signalRHub.listen(this._flux.storesHub.selectionStore.state.selectedAgentPool.id);
            }
        }
    }

    private _markDataReady = (): void => {
        this._dataReady = true;
        this._markTti();
    };

    private _markTti(): void {
        if (this._dataReady && !this._ttiMarked) {
            this.context.pageContext
                .getService<IVssPerformanceService>("IVssPerformanceService")
                .markTimeToInteractive(Telemetry.Area, Telemetry.PoolView);
            this._ttiMarked = true;
        }
    }

    private _getInitialDataReady(): boolean {
        // Data is loaded asyncronously for the Agents, Details, and Security tab
        // Otherwise, data is ready
        switch (this._flux.poolActionCreator.navigationActionCreator.getTabIdParam()) {
            case PoolHubViewKeys.Agents:
            case PoolHubViewKeys.Details:
            case PoolHubViewKeys.Security:
            case PoolHubViewKeys.Diagnostics:
                return false;
            case PoolHubViewKeys.Jobs:
            case PoolHubViewKeys.Checks:
            default:
                return true;
        }
    }
}

VssComponent.register("ms.vss-build-web.admin-pool-view-component", PoolView);
